import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { activityVerb } from "@/lib/activityLabels";
import { queryKeys } from "@/lib/queryKeys";
import { Icon, type IconName } from "@/components/Icon";
import { Skeleton } from "@/components/Skeleton";
import { EmptyState } from "@/components/EmptyState";

// A per-action icon so the inbox is scannable at a glance (comment vs move vs
// create) rather than a wall of identical bells.
function iconForAction(action: string): IconName {
  if (action.startsWith("comment.")) return "chat";
  if (action === "task.created") return "plus";
  if (action === "task.moved") return "board";
  if (action === "task.updated") return "pencil";
  if (action === "task.deleted") return "close";
  if (action.startsWith("project.")) return "folder";
  return "bell";
}

/**
 * /inbox — notification list. Shows recent activity items the current
 * user should know about (assigned, mentioned, etc.).
 */
function Inbox() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: queryKeys.inbox(),
    queryFn: api.inbox,
  });
  const unread = useQuery({
    queryKey: queryKeys.inboxUnread(),
    queryFn: api.inboxUnread,
  });
  const markAll = useMutation({
    mutationFn: api.inboxMarkAllRead,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.inbox() });
      qc.invalidateQueries({ queryKey: queryKeys.inboxUnread() });
    },
  });
  // Resolve actor ids to display names (visibility-scoped to shared
  // projects — exactly the set whose activity can appear here).
  const team = useQuery({ queryKey: queryKeys.team(), queryFn: api.listTeam });
  const nameOf = (id: string | null) => {
    if (!id) return "Someone";
    return team.data?.members.find((m) => m.id === id)?.name ?? id;
  };

  return (
    <div className="pg-wrap" style={{ maxWidth: 820 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          margin: "0 0 18px",
        }}
      >
        <h1 className="t-h1" style={{ margin: 0 }}>
          Notifications
        </h1>
        <div style={{ flex: 1 }} />
        {(unread.data?.unread ?? 0) > 0 && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={markAll.isPending}
            onClick={() => markAll.mutate()}
          >
            {markAll.isPending ? "Marking…" : "Mark all read"}
          </button>
        )}
      </div>

      {q.isLoading ? (
        <Skeleton count={5} height={56} />
      ) : !q.data?.items.length ? (
        <EmptyState
          icon="bell"
          title="All caught up."
          caption="No new notifications."
        />
      ) : (
        <div className="panel">
          {q.data.items.map((item) => (
            <div
              key={item.id}
              className="nd-item"
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "14px 18px",
                borderBottom: "1px solid var(--border)",
              }}
            >
              <div
                className="nd-ico"
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  background: "var(--st-todo-bg)",
                  color: "var(--st-todo-ink)",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                <Icon name={iconForAction(item.action)} size={14} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="t-sm">
                  <b>{nameOf(item.actor_id)}</b> {activityVerb(item.action)}
                </div>
                <div className="t-mono" style={{ marginTop: 2 }}>
                  {new Date(item.created_at ?? Date.now()).toLocaleString()}
                </div>
              </div>
              {item.task_id ? (
                <Link
                  to="/tasks/$taskId"
                  params={{ taskId: item.task_id }}
                  className="t-muted"
                  aria-label="Open task"
                >
                  <Icon name="arrow" />
                </Link>
              ) : (
                <Link
                  to="/projects/$projectId"
                  params={{ projectId: item.project_id }}
                  className="t-muted"
                  aria-label="Open project"
                >
                  <Icon name="arrow" />
                </Link>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute("/inbox")({ component: Inbox });
